import React, { useEffect, useLayoutEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';

/** tekst za pretragu: bez velikih slova i dijakritika (č/ć/š/ž/đ) */
export const norm = (v) => String(v ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'dj');

/** redovi u kojima je SVAKA reč upita (odvojene razmakom) u nekom od polja koja vraća `vrednosti(red)` —
    "petrovic presto" nalazi Petrovića iz Presta, bez obzira na redosled reči i kolonu */
export function filtriraj(rows, upit, vrednosti) {
    const reci = norm(upit).split(/\s+/).filter(Boolean);
    if (!reci.length) return rows;
    return rows.filter((r) => {
        const tekst = vrednosti(r).map(norm).join('\n');
        return reci.every((w) => tekst.includes(w));
    });
}

/** vrednost sa zakašnjenjem (pretraga na serveru tek kad korisnik zastane sa kucanjem) */
export function useOdlozeno(vrednost, ms = 350) {
    const [v, setV] = useState(vrednost);
    useEffect(() => {
        const t = setTimeout(() => setV(vrednost), ms);
        return () => clearTimeout(t);
    }, [vrednost, ms]);
    return v;
}

/** Polje za pretragu tabele (lupa levo), sa brojem pronađenih desno; `children` = akcije tabele (desno, posle broja). */
export default function TableSearch({ value, onChange, placeholder = 'Pretraga...', label, ukupno, pronadjeno, jedinica = '', children }) {
    return (
        <div className="table-toolbar">
            <span className="table-search">
                <i className="pi pi-search" aria-hidden="true" />
                <InputText type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={label || placeholder} />
            </span>
            {ukupno != null && (
                <span className="muted table-count" aria-live="polite">{value.trim() ? `${pronadjeno} od ${ukupno}` : `${ukupno}${jedinica ? ` ${jedinica}` : ''}`}</span>
            )}
            {ukupno == null && pronadjeno != null && (
                <span className="muted table-count" aria-live="polite">{`pronađeno: ${pronadjeno}`}</span>
            )}
            {children && <div className="row-inline table-actions">{children}</div>}
        </div>
    );
}

/** Zajednička podešavanja paginatora tabela pregleda. */
export const PAGINATOR = {
    paginator: true,
    paginatorTemplate: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
    currentPageReportTemplate: '{first}–{last} od {totalRecords}',
};

/**
 * Broj redova po strani tako da tabela sa paginatorom stane na ekran bez skrolovanja.
 * @param ref     omotač oko DataTable
 * @param active  ekran je prikazan (KeepAlive) — samo tada se meri
 * @param rezerva px ispod tabele: paginator, napomena, donji razmak panela
 * @param dep     promena podataka posle koje se meri ponovo (npr. novi rezultat)
 * Visina reda je PROSEČNA stvarna visina (tbody / broj redova podataka): radi i kad se tekst prelama
 * i kad tabela ima zaglavlja grupa (rowGroupMode="subheader").
 */
export function useFitRows(ref, active, { rezerva = 150, min = 5, dep } = {}) {
    const [rows, setRows] = useState(10);
    useLayoutEffect(() => {
        if (!active) return undefined;
        const calc = () => {
            const el = ref.current;
            if (!el) return;
            const head = el.querySelector('thead');
            const body = el.querySelector('tbody');
            const n = el.querySelectorAll('tbody tr:not(.p-datatable-emptymessage):not(.p-rowgroup-header)').length;
            const rowH = (n && body.offsetHeight / n) || 41;
            const avail = window.innerHeight - el.getBoundingClientRect().top - ((head && head.offsetHeight) || 40) - rezerva;
            setRows(Math.max(min, Math.floor(avail / rowH)));
        };
        calc();
        window.addEventListener('resize', calc);
        return () => window.removeEventListener('resize', calc);
    }, [active, dep, rezerva, min]); // eslint-disable-line react-hooks/exhaustive-deps
    return rows;
}

/** Stanje strane klijentske paginacije: nova pretraga -> 1. strana; manje redova -> poslednja postojeća strana. */
export function usePage(ukupno, poStrani, upit) {
    const [first, setFirst] = useState(0);
    useEffect(() => { setFirst(0); }, [upit]);
    useEffect(() => {
        setFirst((f) => (f >= ukupno ? Math.max(0, Math.floor((ukupno - 1) / poStrani) * poStrani) : Math.floor(f / poStrani) * poStrani));
    }, [ukupno, poStrani]);
    return [first, setFirst];
}
